import type { FC } from "react";
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";

import appConfig from "config/appConfig";
import { Scrollbars } from "react-custom-scrollbars-2";

import { useNavigate, useSearchParams } from "react-router-dom";

import type { NavMenuMethodsHandlers } from "shared/Components/NavMenu";
import NavMenu from "shared/Components/NavMenu";
import { useGames, useSystem, useUser } from "shared/hooks";

import useEffectOnce from "shared/hooks/useEffectOnce";
import useLanguage from "shared/hooks/useLanguage";

import MainContent from "./MainContent";

const AuthModal = lazy(() => import("shared/Components/AuthModal"));
const ChangePasswordModal = lazy(() => import("shared/Components/ChangePasswordModal"));
const UnavailableRegionModal = lazy(() => import("shared/Components/UnavailableRegionModal"));
const GameSearchModal = lazy(() => import("shared/Components/GameSearchModal"));
const RestrictedCountriesModal = lazy(() => import("shared/Components/RestrictedCountriesModal"));

const BasicLayout: FC = () => {
	const { getUserInfo, getProfile, getData: getUserData } = useUser();
	const { isAuthorized } = getUserData();

	const navigate = useNavigate();

	const { setAuthDialog, getBootstrap, getCurrencyList, getCountryList, getData: getSystemData } = useSystem();
	const { authDialog } = getSystemData();

	const { switchLanguage, getData: getLanguageData } = useLanguage();
	const { activeLanguage } = getLanguageData();

	const { getGames } = useGames();

	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		switchLanguage(activeLanguage);
	}, [activeLanguage, switchLanguage]);

	const [showSearchGamesDialog, setShowSearchGamesDialog] = useState(false);
	const [showRestrictedCountriesDialog, setShowRestrictedCountriesDialog] = useState(false);
	const [showUnavailableRegionDialog, setShowUnavailableRegionDialog] = useState(false);
	const [changePasswordDialog, setChangePasswordDialog] = useState<{
		open: boolean;
		code: string;
	}>();

	const navMenuRef = useRef<null | NavMenuMethodsHandlers>(null);

	useEffectOnce(() => {
		getBootstrap({});
		getCurrencyList();
		getCountryList();
		getGames({});
	});

	useEffect(() => {
		if (isAuthorized) {
			getUserInfo();
			getProfile();
		}
	}, [isAuthorized, getUserInfo, getProfile]);

	useEffect(() => {
		const resetPasswordCode = searchParams.get("resetPasswordCode");
		const next = searchParams.get("next");
		const login = searchParams.get("login");
		const restricted = searchParams.get("restricted");

		if (restricted !== null) {
			setShowUnavailableRegionDialog(true);
			setSearchParams({});
			return;
		}

		if (!!resetPasswordCode) {
			setChangePasswordDialog({
				open: true,
				code: resetPasswordCode
			});
			setSearchParams({});
			return;
		}

		if (login !== null) {
			setAuthDialog({
				open: true,
				flow: "login",
				next: next
			});
			setSearchParams({});
			return;
		}
	}, [searchParams, setAuthDialog, setSearchParams]);

	const openAuthModal = (flow: "bonus" | "login" | "signUp") => {
		setAuthDialog({
			open: true,
			flow
		});
	};

	const closeAuthModal = () => {
		if (authDialog?.next) {
			navigate(authDialog.next);
		}
		setAuthDialog(undefined);
	};

	const closeUnavailableRegionModal = () => {
		setShowUnavailableRegionDialog(false);
	};

	const closeChangePasswordModal = useCallback(() => {
		setChangePasswordDialog(undefined);
		setSearchParams({});
	}, [setSearchParams]);

	const onToggleMenu = useCallback(() => {
		navMenuRef.current?.toggleNavMenu();
	}, []);

	const toggleSearchGameDialog = () => {
		setShowSearchGamesDialog(show => !show);
	};

	const toggleRestrictedCountriesModal = () => {
		setShowRestrictedCountriesDialog(show => !show);
	};

	return (
		<>
			<div className="w-full h-full flex items-start">
				<NavMenu ref={navMenuRef} onOpenAuthModal={openAuthModal} onOpenGameSearchModal={toggleSearchGameDialog} />
				{appConfig.GLOBAL_CONSTANTS.ENV_STAGE === "local" ? (
					<MainContent
						openAuthModal={openAuthModal}
						onOpenGameSearchModal={toggleSearchGameDialog}
						onToggleMenu={onToggleMenu}
					/>
				) : (
					<Scrollbars height="100%" autoHide>
						<MainContent
							openAuthModal={openAuthModal}
							onOpenGameSearchModal={toggleSearchGameDialog}
							onToggleMenu={onToggleMenu}
						/>
					</Scrollbars>
				)}
			</div>
			{authDialog?.open && (
				<Suspense>
					<AuthModal
						flow={authDialog?.flow}
						onShowRestrictedCountries={toggleRestrictedCountriesModal}
						onClose={closeAuthModal}
					/>
				</Suspense>
			)}
			{showUnavailableRegionDialog && (
				<Suspense>
					<UnavailableRegionModal onClose={closeUnavailableRegionModal} />
				</Suspense>
			)}
			{!!changePasswordDialog?.open && (
				<Suspense>
					<ChangePasswordModal code={changePasswordDialog.code} onClose={closeChangePasswordModal} />
				</Suspense>
			)}
			{showSearchGamesDialog && (
				<Suspense>
					<GameSearchModal onClose={toggleSearchGameDialog} />
				</Suspense>
			)}
			{showRestrictedCountriesDialog && (
				<Suspense>
					<RestrictedCountriesModal onClose={toggleRestrictedCountriesModal} />
				</Suspense>
			)}
		</>
	);
};

export default BasicLayout;
